import type { EditorCanvas } from "replo-utils/lib/misc/canvas";
import type { Animation } from "schemas/animations";
import type { MediaSize } from "schemas/breakpoints";
import type { Component } from "schemas/component";
import type { ComponentStyleProps } from "schemas/runtimeStyles";
import type { GradientStops } from "schemas/solidOrGradient";
import type { RuntimeStyleProperties } from "schemas/styleAttribute";

import upperFirst from "lodash-es/upperFirst";
import {
  convertToReploSizedImageSource,
  isReploSizeableImageUrl,
} from "replo-utils/lib/assets";
import { parseInteger } from "replo-utils/lib/math";
import { styleAttributeToDefaultStyle } from "schemas/styleAttribute";

import { parseUnit } from "schemas/cssUnits";
import {
  isShopifyLiquidImageSource,
  swapShopifyLiquidImageWidth,
} from "../../store/utils/liquid";
import { getExpectedFlexBasis } from "./component";
import {
  allCSSSideNames,
  getDynamicDataCustomPropertyName,
  isEmptyOrAuto,
} from "./css";
import { isDynamicDesignLibraryValue } from "./designLibrary";
import { gradientToCssGradient } from "./gradient";
import { getTransformStyleString } from "./transform";

// Note (Ovishek, 2022-08-29): This empty string means the default styles
// without any media query limit more technically, it represents
// component.props.style.
export const defaultMediaSize = "" as const;

export const MEDIA_MAX_SIZE = {
  sm: 640,
  md: 1024,
  lg: 2400,
} satisfies Record<MediaSize, number>;

const mediaSizesWithDefault = [
  defaultMediaSize,
  "sm",
  "md",
  "lg",
] as const satisfies readonly MediaSizeWithDefault[];
export type MediaSizeWithDefault = MediaSize | typeof defaultMediaSize;

type MediaStyleKey = "style" | `style@${MediaSize}`;

// Note (Noah, REPL-1018): We have this array of media sizes that we actually
// support in the editor which should be used generally when we need to apply
// things for "all" canvas sizes. This is needed for animations because there
// are random issues with the aliased styles overriding actual styles in certain
// cases (see ticket)
export const supportedMediaSizes = [
  "sm",
  "md",
] as const satisfies readonly MediaSize[];
export type SupportedMediaSize = (typeof supportedMediaSizes)[number];
export type SupportedMediaStyleKey = "style" | `style@${SupportedMediaSize}`;

export type SupportedMediaSizeWithDefault =
  | SupportedMediaSize
  | typeof defaultMediaSize;

export const mediaSizeStyles = [
  "style",
  "style@sm",
  "style@md",
] as const satisfies readonly SupportedMediaStyleKey[];

export const mediaSizeStylesToAnimationSize = {
  style: ["lg", "xl"],
  "style@sm": ["sm"],
  "style@md": ["md"],
};

export const allMediaSizeStyles = [
  "style",
  "style@sm",
  "style@md",
  "style@lg",
] as const satisfies readonly MediaStyleKey[];

const mediaSizeToDeviceName = {
  sm: "Mobile",
  md: "Tablet",
  lg: "Laptop",
} as const satisfies Record<MediaSize, string>;

export const getDeviceNameFromMediaSize = (mediaSize: MediaSize) => {
  return mediaSizeToDeviceName[mediaSize];
};

// Note (Ovishek, 2022-07-23): These dependencies are ordered by large to small
// and that's exactly how they are dependent.
const mediaSizeToDependentSizes: Record<MediaSize, MediaSize[]> = {
  sm: ["lg", "md"],
  md: ["lg"],
  lg: [],
};

// Note (Noah, 2021-08-10): We need both min and max widths here because Radium
// doesn't have a stable ordering for media queries so if we don't have them
// sometimes overrides get applied in the wrong order:
// https://github.com/FormidableLabs/radium/issues/879
export const mediaQueries = {
  sm: `(max-width: ${MEDIA_MAX_SIZE.sm}px)`,
  md: `(min-width: ${MEDIA_MAX_SIZE.sm + 1}px) and (max-width: ${MEDIA_MAX_SIZE.md}px)`,
  lg: `(min-width: ${MEDIA_MAX_SIZE.md + 1}px) and (max-width: ${MEDIA_MAX_SIZE.lg}px)`,
} as const satisfies Record<MediaSize, string>;

export const media = {
  sm: `@media ${mediaQueries["sm"]}`,
  md: `@media ${mediaQueries["md"]}`,
  lg: `@media ${mediaQueries["lg"]}`,
} as const satisfies Record<MediaSize, string>;

type MediaQuery = (typeof media)[MediaSize];

const resolveBackgroundImageUrl = (backgroundImage: string) => {
  // Note (Evan, 2024-10-22): A backslash in an image URL will break rendering on published
  // pages (USE-1375). We're also now validating the input in the editor, so this ~should~ never
  // happen, but in case it does, we'll skip rendering the background image so the rest of
  // the page still works.
  if (backgroundImage?.includes("\\")) {
    return "none";
  }
  if (
    backgroundImage &&
    typeof backgroundImage === "string" &&
    !backgroundImage.startsWith("url(") &&
    !backgroundImage.includes("gradient(")
  ) {
    return `url("${backgroundImage}")`;
  }
  return backgroundImage;
};

// NOTE (Matt 2025-03-05): Because the element is not rendered in an iframe, some style attributes need to
// be overridden or updated to "fake" the existence of a window.
function overrideStylesInEditor(
  // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
  style: Record<string, any>,
  config: StyleAliasConfig,
) {
  const styles = { ...style };
  // NOTE (Matt 2025-03-04): In the editor, we need to do some hackery to make "position: fixed" appear
  // correctly. This is because "fixed" is relative to the window, and in the editor the element is
  // just rendered inside of a div- we have to "fake" the window. The solution here is to read the
  // positioning properties and transform properties and ensure we primarily use transform to
  // position the fixed component properly. Additionally, we need a max-width and max-height
  // to ensure the component does not exceed the size of the canvas container.
  if (styles.position === "fixed" && config.isEditor) {
    const transform = style.__transform
      ? { ...style.__transform }
      : {
          translateX: "0px",
          translateY: "0px",
          translateZ: "0px",
          scaleX: "100%",
          scaleY: "100%",
          scaleZ: "100%",
          rotateX: "0deg",
          rotateY: "0deg",
          rotateZ: "0deg",
          skewX: "0deg",
          skewY: "0deg",
        };

    const currentTranslateX = transform.translateX;
    if (styles.left) {
      let leftValue = styles.left;
      // Handle percentage values
      if (leftValue.endsWith("%") || leftValue.endsWith("vw")) {
        const percentage = Number.parseFloat(leftValue);
        leftValue = `calc(var(--canvas-width) * ${percentage / 100})`;
      }
      // Combine with existing translateX
      transform.translateX = currentTranslateX
        ? `calc(${currentTranslateX} + ${leftValue})`
        : leftValue;
    } else if (styles.right) {
      let rightValue = styles.right;

      // Handle percentage values
      if (rightValue.endsWith("%") || rightValue.endsWith("vw")) {
        const percentage = Number.parseFloat(rightValue);
        rightValue = `calc(var(--canvas-width) * ${percentage / 100})`;
      }

      // For right, we need to subtract from canvas width
      transform.translateX = `calc(var(--canvas-width) - ${rightValue} - ${currentTranslateX || "0px"})`;
    }

    // NOTE (Matt 2025-03-04): The reason we need to add `!important` flags here is to override
    // the `!important` flags that are set when we drag-to-preview these values in the editor.
    // That CSS is written using the `previewablePropertyDataMapping` in `getPreviewEditorStyles`.
    // In order to reduce hackery, we just won't allow the drag-to-preview for position fixed
    // items in the editor.
    styles.left = "unset !important";
    styles.right = "unset !important";
    styles.transform = `${getTransformStyleString(transform)} !important`;
    if (styles.bottom) {
      styles.top = `calc(var(--canvas-height) - ${styles.bottom}) !important`;
      styles.bottom = "unset !important";
    } else {
      styles.top = `${styles.top} !important`;
    }
    if (styles.maxWidth) {
      styles.maxWidth = `min(var(--canvas-width), ${styles.maxWidth}) !important`;
    } else {
      styles.maxWidth = "var(--canvas-width) !important";
    }
    if (styles.maxHeight) {
      styles.maxHeight = `min(var(--canvas-height), ${styles.maxHeight}) !important`;
    } else {
      styles.maxHeight = "var(--canvas-height) !important";
    }
  }
  return styles;
}

export function cleanupAliasedStyles(
  // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
  style: Record<string, any>,
  config: StyleAliasConfig,
) {
  const styles = { ...style };
  for (const key of Object.keys(styles)) {
    if (styles[key] == null || key.startsWith("__")) {
      delete styles[key];
    }

    if (config.isEditor) {
      // Note (Ovishek, 2023-02-23): We replace all the relative heights with
      // hardcoded pixel viewport heights. If we don't do this, the iframe does
      // load indefinitely long and it never stops. Viewport heights don't really
      // work inside iframes where, it's height depends on their content. This is
      // a circular dependency on height, and was causing the issue. And in
      // published page we keep the same vh/vw. That means this change only
      // effects in the editor.
      const propertiesAffectVerticalPosition = [
        "height",
        "minHeight",
        "maxHeight",
        "paddingTop",
        "paddingBottom",
        "marginTop",
        "marginBottom",
        "top",
        "bottom",
      ];

      if (propertiesAffectVerticalPosition.includes(key)) {
        styles[key] = calculateViewportRelativeValue(
          styles[key],
          config.viewportHeight,
        );
      }

      // NOTE (Jackson 2025-02-18): In the editor, we are also replacing vw with cqw because
      // vw now references the browser window width instead of the canvas width, cqw uses our
      // @container widths. People may use vw for properties that affect both horizontal and vertical
      // position, so we check for that here to exclude other random CSS properties from being checked
      if (config.isEditor && /vw/.test(styles[key])) {
        styles[key] = styles[key].replaceAll("vw", "cqw");
      }
    }
  }
  if (config.isEditor) {
    return overrideStylesInEditor(styles, config);
  }
  return styles;
}

// TODO (Gabe 2023-06-15): improve the types here
export function getAliasedStyles({
  styles,
  mediaSizeStyles,
  mediaSize,
  animations,
  config,
}: {
  styles: RuntimeStyleProperties;
  mediaSizeStyles: RuntimeStyleProperties;
  mediaSize: MediaSizeWithDefault;
  animations: Animation[];
  config: StyleAliasConfig;
}): RuntimeStyleProperties {
  if (!styles) {
    return {};
  }

  // Note (Noah, 2022-04-07): Make a copy of default styles since we're going to
  // mutate resolvedStyles and we don't want to accidentally update the original object
  const resolvedStyles: RuntimeStyleProperties & {
    "--replo-margin-left"?: RuntimeStyleProperties["marginLeft"];
    "--replo-margin-right"?: RuntimeStyleProperties["marginRight"];
    "--replo-margin-top"?: RuntimeStyleProperties["marginTop"];
    "--replo-margin-bottom"?: RuntimeStyleProperties["marginBottom"];
  } = Object.assign({}, mediaSizeStyles, styles);

  if (styles.__display === "none") {
    resolvedStyles.display = "none";
  }

  // Note (Noah, 2022-08-17, REPL-3053): There's some issue with templates where
  // sometimes apparently some images have display: inline. We never want this - we
  // should fix it on the template side, but for now we always reset the display
  // to flex
  if (styles.display === "inline") {
    resolvedStyles.display = "flex";
  }

  if (styles.position === "__alchemy:stickyToHeader") {
    resolvedStyles.position = "sticky";
    const target = resolvedStyles.top ?? null;
    const parsedUnit = parseUnit(target, { value: 0, unit: "px" }, "top", "px");
    if (parsedUnit.unit === "px") {
      const fullPageOffset = config.fullPageOffset;
      parsedUnit.value = parseInteger(`${parsedUnit.value}`) + fullPageOffset;
      resolvedStyles.top = `${parsedUnit.value}${parsedUnit.unit}`;
    }
  }

  if (styles.__animateVariantTransitions) {
    resolvedStyles.transition = "all 300ms cubic-bezier(0.4, 0, 0.2, 1)";
  }

  // If border-radius is set, we need to check overflow related styles
  if (
    Object.keys(styles).some((styleRule) => {
      return [
        "borderRadius",
        "borderTopLeftRadius",
        "borderTopRightRadius",
        "borderBottomLeftRadius",
        "borderBottomRightRadius",
      ].includes(styleRule);
    })
  ) {
    if (styles.overflow) {
      if (!styles.overflow.includes("visible")) {
        // Note (Martin, 2023-04-11, USE-58): Workaround for Safari to make
        // border-radius and overflow to work together. This ensures things like
        // Youtube Embed components correctly cut corners off in Safari if a
        // border-radius is defined.
        // https://gist.github.com/ayamflow/b602ab436ac9f05660d9c15190f4fd7b
        // @ts-expect-error because this is non-standard
        resolvedStyles["WebkitMaskImage"] =
          "-webkit-radial-gradient(white, black)";
      }
    } else {
      // Note (Sebas, 2022-09-08): If no overflow is set, we need to set
      // overflow: 'hidden' for children to respect the border radius.
      resolvedStyles.overflow = "hidden";
    }
  }

  for (const property of ["width", "color"]) {
    // Note (Chance 2023-07-14) This is faster than startCase, perfectly fine
    // for this use case
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    const prop = upperFirst(property) as "Width" | "Color";
    const shorthandProperty = `border${prop}` as const;
    if (
      allCSSSideNames("border", prop).every((property) => {
        return Boolean(resolvedStyles[property]);
      }) &&
      resolvedStyles[shorthandProperty]
    ) {
      delete resolvedStyles[shorthandProperty];
    }
    if (styles[shorthandProperty]) {
      for (const property of allCSSSideNames("border", prop)) {
        if (!styles[property]) {
          // Note (Noah, 2023-10-13): Without the "as", this apparently
          // produces a type which is too complex for typescript to represent.
          // No problem, casting to the key type works fine for this case
          // Legacy cast - may or may not be necessary
          // eslint-disable-next-line replo/no-as-cast
          resolvedStyles[property as keyof RuntimeStyleProperties] =
            styles[shorthandProperty];
        }
      }
    }
  }

  if (styles.textAlign) {
    // Note (Noah, 2021-08-21): Set textAlignLast because it's necessary to style
    // text in <select> elements. This is fine because we don't support aligning
    // the last line of text in Replo otherwise. See for more info:
    // https://stackoverflow.com/questions/35654636/text-align-center-placeholder-text-in-select/35655402
    resolvedStyles.textAlignLast = styles.textAlign;
  }

  if (styles.color === "alchemy:gradient") {
    if (styles.__reploGradient__color__design_library) {
      resolvedStyles.backgroundImage =
        styles.__reploGradient__color__design_library;
    } else {
      let tilt = styles["__alchemyGradient__color__tilt"] ?? "0deg";
      tilt = tilt.toString();
      // Note (Chance 2024-07-14) If stops is not an array or is empty, we will
      // assign it below so this cast is safe.
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      let stops = styles["__alchemyGradient__color__stops"] as GradientStops;
      if (!Array.isArray(stops) || stops.length === 0) {
        stops = [{ color: "#000000", location: "0%" }];
      }
      resolvedStyles.backgroundImage = gradientToCssGradient({ tilt, stops });
      resolvedStyles.backgroundColor = "none";
    }

    resolvedStyles.backgroundSize = "100%";

    // @ts-expect-error because these are non-standard
    resolvedStyles["-webkitTextFillColor"] = "transparent";
    // @ts-expect-error
    resolvedStyles["-webkitBackgroundClip"] = "text";
    // @ts-expect-error
    resolvedStyles["-mozBackgroundClip"] = "text";
  }

  if (styles.backgroundColor === "alchemy:gradient") {
    if (styles.__reploGradient__color__design_library) {
      const resolvedGradientCss = styles.__reploGradient__color__design_library;
      const resolvedBackgroundImage = styles.backgroundImage
        ? resolveBackgroundImageUrl(styles.backgroundImage)
        : null;

      // Note (Sebas, 2022-08-08): In case we have both an image and a gradient as background,
      // we need to combine them in the same prop.
      const resolvedBackgroundValue = resolvedBackgroundImage
        ? `${resolvedBackgroundImage}, ${resolvedGradientCss}`
        : resolvedGradientCss;

      resolvedStyles.backgroundImage = resolvedBackgroundValue;
      resolvedStyles.backgroundColor = resolvedBackgroundValue;
    } else {
      let tilt = styles["__alchemyGradient__backgroundColor__tilt"] ?? "0deg";
      tilt = tilt.toString();

      // Note (Chance 2024-07-14) If stops is not an array or is empty, we will
      // assign it below so this cast is safe.
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      let stops = styles[
        "__alchemyGradient__backgroundColor__stops"
      ] as GradientStops;
      if (!Array.isArray(stops) || stops.length === 0) {
        stops = [{ color: "#000000", location: "0%" }];
      }

      const resolvedGradientCss = gradientToCssGradient({ tilt, stops });
      const resolvedBackgroundImage = styles.backgroundImage
        ? resolveBackgroundImageUrl(styles.backgroundImage)
        : null;

      // Note (Sebas, 2022-08-08): In case we have both an image and a gradient as background,
      // we need to combine them in the same prop.
      const resolvedBackgroundValue = resolvedBackgroundImage
        ? `${resolvedBackgroundImage}, ${resolvedGradientCss}`
        : gradientToCssGradient({ tilt, stops });

      resolvedStyles.backgroundImage = resolvedBackgroundValue;
      resolvedStyles.backgroundColor = styles.backgroundColor ?? stops[0].color;
    }
    resolvedStyles.backgroundSize = styles.backgroundSize ?? "100%";
  }

  // TODO (Martin, 2023-09-29): Move this to getResolvedStyles and resolveStyleRule
  // since it's mostly related about resolving dynamic data.
  if (
    styles.color?.startsWith("{{") &&
    !isDynamicDesignLibraryValue(styles.color)
  ) {
    const dynamicDataKey = getDynamicDataCustomPropertyName(styles.color);
    resolvedStyles.backgroundColor = `var(${dynamicDataKey}-background-color, ${
      styles.backgroundColor?.startsWith("{{")
        ? `var(${getDynamicDataCustomPropertyName(
            styles.backgroundColor,
          )}-background-color, transparent)`
        : (styles.backgroundColor ?? "transparent")
    })`;
    resolvedStyles.backgroundImage = `var(${dynamicDataKey}-background-image, ${
      styles.backgroundImage?.startsWith("{{")
        ? `var(${getDynamicDataCustomPropertyName(
            styles.backgroundImage,
          )}, none)`
        : (styles.backgroundImage ?? "none")
    })`;

    // @ts-expect-error
    resolvedStyles["-webkitBackgroundClip"] =
      `var(${dynamicDataKey}-background-clip, border-box)`;
    // @ts-expect-error
    resolvedStyles["-mozBackgroundClip"] =
      `var(${dynamicDataKey}-background-clip, border-box)`;
    // @ts-expect-error
    resolvedStyles["-webkitTextFillColor"] =
      `var(${dynamicDataKey}-webkit-text-fill-color, currentcolor)`;
  } else if (
    styles.backgroundColor?.startsWith("{{") &&
    !isDynamicDesignLibraryValue(styles.backgroundColor)
  ) {
    const dynamicDataKey = getDynamicDataCustomPropertyName(
      styles.backgroundColor,
    );

    resolvedStyles.backgroundColor = `var(${dynamicDataKey}-background-color, transparent)`;
    resolvedStyles.backgroundImage = `var(${dynamicDataKey}-background-image, ${
      styles.backgroundImage?.startsWith("{{")
        ? `var(${getDynamicDataCustomPropertyName(
            styles.backgroundImage,
          )}, none)`
        : (styles.backgroundImage ?? "none")
    })`;
  }

  if (
    styles.backgroundColor !== "alchemy:gradient" &&
    styles.color !== "alchemy:gradient" &&
    !styles.color?.startsWith("{{") &&
    !styles.backgroundColor?.startsWith("{{") &&
    styles.backgroundImage
  ) {
    // Note (Sebas, 2022-08-16): In case there is no gradient and the user
    // selects a dynamic image we need to set a valid url for it to work.
    // Also we need to check if the color of the font is not a gradient
    // (only valid for text components) to not override it.
    resolvedStyles.backgroundImage = resolveBackgroundImageUrl(
      styles.backgroundImage,
    );
  }

  // NOTE (Martin, 2024-03-05, USE-764, USE-784): We need to explicitly set
  // background-image to none if background-color is set, it's not a gradient
  // nor dynamic and there's no background-image set. This is because if we
  // don't do this, background-image could be inherited from base styles on
  // media queries and that will interfere with what the user set to be the
  // background color.
  // TODO (Martin, 2024-03-08): We should abstract the checking below, along
  // with the ones above into some helper utils.
  if (
    styles.backgroundColor &&
    styles.backgroundColor !== "alchemy:gradient" &&
    !resolvedStyles.backgroundImage
  ) {
    resolvedStyles.backgroundImage = "none";
  }

  // Note (Noah, 2021-09-15): Always set flexShrink on ALL components, because
  // we never want the flexbox behavior where elements shrink if they have defined
  // content widths that are greater than their parent - we always want them to
  // overflow (and the user can specify overflow behavior)
  if (styles.flexGrow && styles.flexGrow !== "unset") {
    // Note (Evan, 2024-07-29): Use the flex shrink value from styles, if there
    // is one (currently just for Figma imports), fall back to the flex grow
    // value.
    resolvedStyles.flexShrink = styles.__flexShrink ?? styles.flexGrow;

    // Set flexBasis so that the containers don't expand based on their intrinsic
    // content
    // https://css-tricks.com/equal-columns-with-flexbox-its-more-complicated-than-you-might-think/
    resolvedStyles.flexBasis = getExpectedFlexBasis({
      hasFlexGrow: true,
      parentFlexDirection: styles.__parentFlexDirection,
      parentHasDefinedWidth: styles.__parentHasWidth,
      parentHasDefinedHeight: styles.__parentHasHeight,
    });
  } else {
    // Note (Chance 2023-06-30) If there is no flex-grow, the flex item should
    // never shrink when the element has a defined width, per our comment above
    // the if statement.
    resolvedStyles.flexShrink = styles.__flexShrink ?? 0;

    // Note (Noah, 2022-06-13, REPL-2605, REPL-2610): We always want flex-basis to be auto
    // if there's a width set, otherwise we can get into situations where there was a flex-basis
    // set from before, but now we set the flex-grow to "unset" because we set a defined width,
    // but flex-basis is 0 or something which causes a lot of issues.
    resolvedStyles.flexBasis = "auto";
  }

  // Note (Noah, 2022-07-18, REPL-2978): If a container is supposed to fill available
  // space, we need to make sure to set min-width/height to 0. This is because
  // flex containers are not allowed to shrink below their min-width/height, and
  // the default min-width/height is auto, which means with enough content, a container
  // will expand beyond the available space it's filling. Setting min-width to 0 makes
  // the "fill available space" work as expected.
  //
  // HOWEVER, this only makes sense if the flex-wrap is set to not wrap. If it's wrap,
  // then we don't want to set any min-height/width, because it will prevent the wrap
  // logic from working correctly. So, we use a css var to set the min-width/height to 0px
  // UNLESS we specifically have flex-wrap: wrap on a parent.
  //
  // See: https://stackoverflow.com/questions/73016104/flexbox-growing-div-with-flex-basis-0px-expands-too-far-when-theres-lots-of-c
  if (styles.flexWrap) {
    // Note (Chance 2023-07-14) We can stop expecting this error when we augment
    // React.CSSProperties to include our custom properties
    // @ts-expect-error
    resolvedStyles["--replo-flex-min-dimension"] =
      styles.flexWrap === "wrap" ? "auto" : "0px";
  }

  // NOTE (Chance 2024-02-23): We want to set min-width or min-height to 0px
  // depending on the parent's flex-direction if it is not explicitly set. This
  // keeps the flex item from expanding beyond the available space. We evaluate
  // each property first by looking at resolvedStyles, as it may have been
  // previously overridden earlier in the function. Otherwise we evaluate
  // mergedStyles to see if it was set otherwise by the component definition or
  // in some dependant styles.
  const parentHasWidth =
    resolvedStyles.__parentHasWidth ?? styles.__parentHasWidth;
  const parentHasHeight =
    resolvedStyles.__parentHasHeight ?? styles.__parentHasHeight;
  const parentFlexDirection =
    resolvedStyles.__parentFlexDirection ?? styles.__parentFlexDirection;
  const flexBasis = resolvedStyles.flexBasis ?? styles.flexBasis;
  const flexGrow = resolvedStyles.flexGrow ?? styles.flexGrow;
  const minHeight = resolvedStyles.minHeight ?? styles.minHeight;
  const minWidth = resolvedStyles.minWidth ?? styles.minWidth;

  if (
    parentFlexDirection === "row" &&
    flexBasis === 0 &&
    flexGrow &&
    isEmptyOrAuto(minWidth) &&
    parentHasWidth
  ) {
    resolvedStyles.minWidth = "var(--replo-flex-min-dimension, 0px)";
  } else if (
    parentFlexDirection === "column" &&
    flexBasis === 0 &&
    flexGrow &&
    isEmptyOrAuto(minHeight) &&
    parentHasHeight
  ) {
    resolvedStyles.minHeight = "var(--replo-flex-min-dimension, 0px)";
  }

  for (const [entry, value] of Object.entries(resolvedStyles)) {
    if (value == null || value == "alchemy:gradient") {
      // 'string' can't be used to index type 'RuntimeStyleProperties'
      // but this is fine
      // @ts-expect-error
      delete resolvedStyles[entry];
    }
  }

  if (
    config.alwaysHideOverflowYIfPossible &&
    styles.height &&
    styles.height !== "auto"
  ) {
    resolvedStyles.overflowY = "hidden";
  }

  if (styles.__flexGap) {
    resolvedStyles.rowGap = styles.__flexGap;
    resolvedStyles.columnGap = styles.__flexGap;
    if (config.addGapCssVariable) {
      // Note (Chance 2023-07-14) We can stop expecting this error when we
      // augment React.CSSProperties to include our custom properties
      // @ts-expect-error
      resolvedStyles["--replo-gap"] = styles.__flexGap;
    }
  }

  if (styles.display === "grid" && (styles.rowGap || styles.columnGap)) {
    const rowGap = styles.rowGap || "0px";
    const columnGap = styles.columnGap || "0px";
    resolvedStyles.gap = `${rowGap} ${columnGap}`;
    // NOTE (Fran 2024-03-19): We should delete rowGap and columnGap from the resolvedStyles
    // to avoid conflicts with the gap property.
    delete resolvedStyles.rowGap;
    delete resolvedStyles.columnGap;
    // Note (Chance 2023-07-14) We can stop expecting this error when we augment
    // React.CSSProperties to include our custom properties
    // @ts-expect-error
    resolvedStyles["--replo-gap"] = columnGap;
  }

  if (mediaSizeStyles?.__animation) {
    const animation = animations?.find((animation) => {
      return animation.devices.includes(mediaSize || "lg");
    });
    for (const [key, value] of Object.entries(mediaSizeStyles.__animation)) {
      if (key === "animationName") {
        const shouldBeAnimated = animation && !config.isEditor;
        // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        resolvedStyles[key as keyof RuntimeStyleProperties] = shouldBeAnimated
          ? value
          : "none";
      } else {
        // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        resolvedStyles[key as keyof RuntimeStyleProperties] = value;
      }
    }
    resolvedStyles["animationPlayState"] =
      animation?.trigger.type === "onViewportEnter" ? "paused" : "running";
    if (animation?.runOnlyOnce === false) {
      resolvedStyles["animationIterationCount"] = "infinite";
    }
  }

  if (styles.__textStroke) {
    // @ts-expect-error
    resolvedStyles["-webkit-text-stroke"] = styles.__textStroke;
  }

  // Note (Noah, 2023-06-30, REPL-7839): If we have a legacy __alchemyRotation
  // value, then default to that and don't add in any transform value (if the
  // user ever sets a different transform value via the editor, the
  // __alchemyRotation value will be removed). However, default to the
  // __alchemyRotation value if it exists on a dependent canvas size (as long as
  // the current canvas size doesn't have a transform value set - otherwise we
  // would override it that transform value, which we don't want to do)
  const alchemyRotationValue =
    resolvedStyles.__alchemyRotation ??
    (!resolvedStyles.__transform ? styles.__alchemyRotation : null);
  if (alchemyRotationValue) {
    resolvedStyles.__transform = Object.assign(
      {},
      {
        translateX: "0px",
        translateY: "0px",
        translateZ: "0px",
        scaleX: "100%",
        scaleY: "100%",
        scaleZ: "100%",
        rotateX: "0deg",
        rotateY: "0deg",
        rotateZ: "0deg",
        skewX: "0deg",
        skewY: "0deg",
      },
      resolvedStyles.__transform,
      { rotateZ: alchemyRotationValue },
    );
  }

  if (resolvedStyles.__transform) {
    resolvedStyles.transform = getTransformStyleString(
      resolvedStyles.__transform,
    );
  }

  if (!styles.position && config.defaultToPositionRelative) {
    resolvedStyles.position = "relative";
  }

  // # region Force max-width/height dimensions to fill available space in a flex container

  // NOTE (Fran 2024-09-19, USE-1306): In certain cases, align-self: stretch doesn't work when trying to
  // make a component take up the entire space of its parent. This can cause issues like USE-1306,
  // where the user is trying to center a container with max-width inside a flexbox container with
  // center or end alignment. Therefore, we should force the component to take up the entire space.
  // https://fathom.video/share/GQhKSYWjhr7QdBBdyxKghGJt5syVjLsb
  const isInsideAVerticalDirectionFlexboxContainer =
    styles.__parentFlexDirection === "column" &&
    styles.__parentAlignItems &&
    !["start", "flex-start"].includes(styles.__parentAlignItems);

  if (
    styles.alignSelf === "stretch" &&
    isInsideAVerticalDirectionFlexboxContainer &&
    styles.maxWidth &&
    styles.maxWidth !== "100%"
  ) {
    // Note (Noah, 2026-01-01, USE-2274): We need to explicitly set the margin CSS variables
    // to 0px if no margin is defined, otherwise they can inherit from outer scopes and
    // cause incorrect width calculations.
    resolvedStyles["--replo-margin-left"] =
      resolvedStyles.marginLeft === "auto"
        ? "0px"
        : (resolvedStyles.marginLeft ??
          styleAttributeToDefaultStyle.marginLeft);
    resolvedStyles["--replo-margin-right"] =
      resolvedStyles.marginRight === "auto"
        ? "0px"
        : (resolvedStyles.marginRight ??
          styleAttributeToDefaultStyle.marginRight);
    resolvedStyles.width =
      "calc(100% - (var(--replo-margin-left, 0px) + var(--replo-margin-right, 0px)))";
    resolvedStyles.alignSelf = "auto";
  }

  const isInsideAHorizontalDirectionFlexboxContainer =
    styles.__parentFlexDirection === "row" &&
    styles.__parentAlignItems &&
    !["start", "flex-start"].includes(styles.__parentAlignItems);

  if (
    styles.alignSelf === "stretch" &&
    isInsideAHorizontalDirectionFlexboxContainer &&
    styles.maxHeight &&
    styles.maxHeight !== "100%"
  ) {
    // Note (Noah, 2026-01-01, USE-2274): We need to explicitly set the margin CSS variables
    // to 0px if no margin is defined, otherwise they can inherit from outer scopes and
    // cause incorrect height calculations.
    resolvedStyles["--replo-margin-top"] =
      resolvedStyles.marginTop === "auto"
        ? "0px"
        : (resolvedStyles.marginTop ?? styleAttributeToDefaultStyle.marginTop);
    resolvedStyles["--replo-margin-bottom"] =
      resolvedStyles.marginBottom === "auto"
        ? "0px"
        : (resolvedStyles.marginBottom ??
          styleAttributeToDefaultStyle.marginBottom);
    resolvedStyles.height =
      "calc(100% - (var(--replo-margin-top, 0px) + var(--replo-margin-bottom, 0px)))";
    resolvedStyles.alignSelf = "auto";
  }

  // #endregion

  // NOTE (Fran 2024-12-16): We are adding css variables for all the font families that use design library.
  // We don't need to do this because we directly use the saved style value.
  if (styles.fontFamily && !isDynamicDesignLibraryValue(styles.fontFamily)) {
    // NOTE (Fran 2024-11-05 USE-1410): We need to wrap the font name in quotes
    // because it may contain spaces or any special characters that can
    // cause issues in a Replo Element.
    // Handle font family names that may contain special characters
    resolvedStyles.fontFamily = styles.fontFamily
      .split(",")
      .map((font) => {
        // NOTE (Fran 2024-11-06 USE-1421): If the font family name contains spaces, we need to wrap it in quotes.
        if (/\s/g.test(font)) {
          // NOTE (Fran 2024-11-06 USE-1421): We need to remove the quotes from the font family name because they are already escaped.
          return `"${font.replace(/(^"|"$)/g, "")}"`;
        }

        // Escape quotes and backslashes
        return font.replace(/(["'\\])/g, "\\$1");
      })
      .join(", ");
  }

  return resolvedStyles;
}

export type StyleAliasConfig = {
  /**
   * If true and the element has height, will automatically set overflow-y to hidden
   * (necessary for text).
   */
  alwaysHideOverflowYIfPossible: boolean;

  /**
   * If true, position: relative will be added if the component does not otherwise
   * specify position. Useful for components which can have children.
   */
  defaultToPositionRelative: boolean;

  /**
   * If true, a css var will be added for the flex-gap property (if it exists). Useful
   * for when things like CarouselV3 need to reference the gap property using a css var.
   */
  addGapCssVariable: boolean;
  fullPageOffset: number;
  isEditor?: boolean;
  viewportHeight?: number;
};

/**
 * This function converts viewport relative sizes to pixel values
 * Using window innerHeight/Width calculation
 */
const calculateViewportRelativeValue = (
  value: string,
  viewportHeight?: number,
) => {
  if (
    !value ||
    typeof value !== "string" ||
    // Note (Ovishek, 2023-02-23): This regular expression tests if the style
    // value is relative to viewport height for example 100vh, 30vh will be
    // passed but not 100% or 100px.
    !/^\d+vh$/.test(value)
  ) {
    return value;
  }

  return `${
    // Note (Ovishek, 2023-02-23): This 1400 is a safe fallback height if there's no window element in any time frame
    // And there's no viewport height
    (Number.parseFloat(value) / 100) * (viewportHeight ?? 1400)
  }px`;
};

function findDependents(mediaSize: MediaSize) {
  const dependents: MediaSize[] = [];
  Object.entries(mediaSizeToDependentSizes).forEach(([key, value]) => {
    if (value.includes(mediaSize)) {
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      dependents.push(key as MediaSize);
    }
  });

  return dependents.reverse();
}

export const editorCanvasToMediaSize: Record<
  EditorCanvas,
  { mediaSize: MediaSize; dependents: MediaSize[] }
> = {
  desktop: {
    mediaSize: "lg",
    dependents: findDependents("lg"),
  },
  tablet: { mediaSize: "md", dependents: findDependents("md") },
  mobile: { mediaSize: "sm", dependents: findDependents("sm") },
};

export const mediaSizeToEditorCanvas: Record<MediaSize, EditorCanvas> = {
  lg: "desktop",
  md: "tablet",
  sm: "mobile",
};

const mediaSizeToImageSize: Record<MediaSize, string> = {
  lg: "1800",
  md: "1024",
  sm: "820",
};

/**
 * Checks if the image source is a Shopify or Replo sizable image
 * url, or if it's a shopify liquid image expression
 */
export const isShopifyOrReploSizeableImage = (imageSrc: string | undefined) => {
  return (
    isShopifyLiquidImageSource(imageSrc) ||
    isShopifySizeableImageUrl(imageSrc) ||
    isReploSizeableImageUrl(imageSrc)
  );
};

/**
 * This function converts an image url to shopify sized image url based on the
 * canvas is being used, but keeps the same url if it's not from shopify
 */
export const convertToShopifyOrReploSizedImageSource = (
  imageSource: string | undefined,
  mediaSize: MediaSize | null,
  width?: React.CSSProperties["width"] | null,
) => {
  if (!imageSource) {
    return imageSource;
  }

  let newImageSource = imageSource;

  const parsedWidth =
    typeof width === "number" ? width : Number.parseInt(width ?? "");
  const mediaWidth = mediaSize
    ? Number.parseInt(mediaSizeToImageSize[mediaSize])
    : null;

  let newWidth: number | null = null;

  /**
   * Note (Ovishek, 2023-01-02): This means how much quality we want. So, by 2x, we link to the image as
   * double of the fixed width/height, if it has any. For example, if we have a fixed width
   * image with width = 100px then we fetch the image with width = 200px which keeps the quality of the image.
   */
  const IMAGE_QUALITY_FACTOR = 2;

  if (
    typeof width === "string" &&
    width?.includes("px") &&
    !Number.isNaN(parsedWidth)
  ) {
    const qualityWidth = parsedWidth * IMAGE_QUALITY_FACTOR;
    newWidth = Math.min(qualityWidth, mediaWidth ?? Number.MAX_SAFE_INTEGER);
  } else if (mediaWidth) {
    newWidth = mediaWidth;
  }

  if (!newWidth) {
    return imageSource;
  }
  if (isReploSizeableImageUrl(imageSource)) {
    return convertToReploSizedImageSource({
      imageSource,
      width: newWidth,
    });
  }

  if (isShopifySizeableImageUrl(imageSource)) {
    if (!imageSource.includes("?")) {
      newImageSource += "?";
    } else if (
      imageSource.charAt(imageSource.length - 1) !== "?" &&
      imageSource.charAt(imageSource.length - 1) !== "&"
    ) {
      newImageSource += "&";
    }
    return `${newImageSource}width=${newWidth}`;
  }
  return swapShopifyLiquidImageWidth(imageSource, newWidth);
};

/**
 * Returns the style value for the given media size
 */
export const getMediaSizeStyleAttributeValue = (
  attribute: keyof RuntimeStyleProperties,
  props: Component["props"],
  mediaSize: MediaSizeWithDefault,
) => {
  if (mediaSize === "") {
    return props.style?.[attribute];
  }
  const sizes = mediaSizeToDependentSizes[mediaSize].concat(mediaSize);
  // NOTE (Chance 2024-02-22): We need to loop through the sizes in reverse
  // order because larger sizes are prioritized.
  for (let index = sizes.length - 1; index >= 0; index--) {
    const size = sizes[index]!;
    const value = props[`style@${size}`]?.[attribute];
    if (value != null) {
      return value;
    }
  }
  return props.style?.[attribute];
};

/**
 * Returns the media resolved styles for selected mapping, for example
 * {
 *   width: "10px",
 *   "@media (max-width: 640px)": {
 *      width: "5px",
 *   }
 * }
 */
// TODO (Gabe 2023-06-15): properly type the styles (if we plan to continue
// using this function)
export const mapMediaCanvasStyles = (
  // TODO (Noah, 2023-02-27, REPL-6462): Some older pages have components without props
  // (specifically Studs - I think this may only be a problem with Studs but I'm not sure).
  // Typescript is actually wrong here - most likely we should run a Distributed Element Query
  // and manually update all components which don't have props to have an empty object instead.
  // For now, to get around issues on their Piercing Services page, we assume this can be
  // undefined
  componentProps: Component["props"] | undefined,
  mapStyles: (
    // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
    styles: Record<string, any>,
    mediaSize: MediaSizeWithDefault,
    // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
  ) => Record<string, any>,
) => {
  // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
  let allMediaStyles: Record<string, any> = {};
  for (const mediaSize of mediaSizesWithDefault) {
    const styleProp = mediaSize ? (`style@${mediaSize}` as const) : "style";
    let mediaStyles = componentProps?.[styleProp] ?? {};
    if (mediaSize && componentProps) {
      mediaStyles = mediaSizeToDependentSizes[mediaSize]
        .concat(mediaSize)
        .reduce((resolvedStyles, currentMediaSize) => {
          const mediaStyles = componentProps[`style@${currentMediaSize}`];
          if (!mediaStyles || Object.keys(mediaStyles).length === 0) {
            return resolvedStyles;
          }
          return Object.assign({}, resolvedStyles, mediaStyles);
        }, componentProps.style ?? {});
    }

    const mappedStyles = mapStyles(mediaStyles, mediaSize);
    if (mediaSize) {
      allMediaStyles[media[mediaSize]] = mappedStyles;
    } else {
      allMediaStyles = { ...allMediaStyles, ...mappedStyles };
    }
  }
  return allMediaStyles;
};

/**
 * Returns a copy of the component's props but only containing styles.
 * E.g. {"style": ..., "style@md": ..., ...}
 */
export function getStylePropsFromComponent(component: Component) {
  const styles: ComponentStyleProps = {};
  for (const key of mediaSizeStyles) {
    // NOTE (Gabe, 2025-06-05 USE-1917): The types seem to be wrong in some
    // cases, but fixing them causes a million type errors.
    if (component.props?.[key]) {
      styles[key] = component.props[key];
    }
  }
  return styles;
}

/**
 * Build the styles object for a media size taking into account media size
 * dependencies. Can also ignore base styles as starting point if set.
 */
export function getMediaSizeStyles(
  componentStyleProps: ComponentStyleProps,
  mediaSize: MediaSize,
  ignoreBaseStyles?: boolean,
): RuntimeStyleProperties {
  const styles = Object.assign(
    {},
    ignoreBaseStyles ? {} : componentStyleProps?.style,
  );
  const allSizes = mediaSizeToDependentSizes[mediaSize].concat(mediaSize);
  for (const size of allSizes) {
    const mediaStyles = componentStyleProps[`style@${size}`];
    if (!mediaStyles) {
      continue;
    }

    for (const key in mediaStyles) {
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      styles[key as keyof RuntimeStyleProperties] =
        // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        mediaStyles[key as keyof RuntimeStyleProperties];
    }
  }

  return styles;
}

/**
 * Callback for mapComponentStyleProps
 *
 * @param mergedMediaSizeStyleRules - Merged styles for all media sizes. This is the final
 * styles object which can be passed to JSS to generate a style sheet.
 * e.g. {"backgroundColor": "red"}
 * @param mediaSize - The media size which was resolved (e.g. "sm")
 * @param onlyThisMediaSizeStyleRules - Styles which were directly extracted from this
 * media size and have NOT been merged with any dependent styles. Useful if you want to
 * dynamically add/merge these styles yourself
 */
type MapComponentStylePropsCallback = (
  mergedMediaSizeStyleRules: RuntimeStyleProperties,
  mediaSize: MediaSizeWithDefault,
  onlyThisMediaSizeStyleRules: RuntimeStyleProperties,
) => RuntimeStyleProperties;

type ForEachStylePropsCallback = (
  mergedMediaSizeStyleRules: RuntimeStyleProperties,
  mediaSize: MediaSizeWithDefault,
  onlyThisMediaSizeStyleRules: RuntimeStyleProperties,
) => void;

type MapComponentStylePropsOptions = {
  ignoreBaseStyles?: boolean;
};

type RuntimeStylePropertiesWithMediaQueries = {
  [Query in MediaQuery]: RuntimeStyleProperties;
};

/**
 * Map over component styles props and run a callback function on each media
 * size considering the dependent sizes for each one unless they are set to be
 * ignored. Can also ignore base styles as starting point when calculating
 * styles for media queries.
 */
export function mapComponentStyleProps(
  componentStyleProps: ComponentStyleProps | undefined,
  callbackFn: MapComponentStylePropsCallback,
  opts: MapComponentStylePropsOptions = {},
) {
  if (!componentStyleProps) {
    return {};
  }

  const { ignoreBaseStyles } = opts;
  const styles: ComponentStyleProps = {};

  // Base styles
  if (componentStyleProps.style) {
    styles["style"] = callbackFn(
      componentStyleProps.style,
      "",
      componentStyleProps.style,
    );
  }

  // Media sizes styles
  for (const mediaSize of supportedMediaSizes) {
    const onlyThisMediaSizeStyles =
      componentStyleProps[`style@${mediaSize}`] ?? {};
    const mediaStyles = getMediaSizeStyles(
      componentStyleProps,
      mediaSize,
      ignoreBaseStyles,
    );
    styles[`style@${mediaSize}`] = callbackFn(
      mediaStyles,
      mediaSize,
      onlyThisMediaSizeStyles,
    );
  }

  return styles;
}

export function forEachComponentStyleProps(
  componentStyleProps: ComponentStyleProps | undefined,
  callbackFn: ForEachStylePropsCallback,
) {
  mapComponentStyleProps(
    componentStyleProps,
    (...args) => {
      callbackFn(...args);
      return {};
    },
    {},
  );
}

/**
 * Convert component style props to a style object with media queries
 */
export function convertComponentStylePropsToStyleRules(
  componentStyleProps: ComponentStyleProps | undefined,
): RuntimeStylePropertiesWithMediaQueries {
  if (!componentStyleProps) {
    return {};
  }

  let styles: RuntimeStylePropertiesWithMediaQueries = {};

  // Base styles
  if (componentStyleProps?.style) {
    styles = componentStyleProps.style;
  }

  // Media sizes styles
  for (const mediaSize of supportedMediaSizes) {
    const mediaStyles = componentStyleProps[`style@${mediaSize}`];
    if (mediaStyles) {
      styles[media[mediaSize]] = mediaStyles;
    }
  }

  return styles;
}

/**
 * Map over component style props, run a callback and then convert result
 * into a style object with media queries
 */
export function mapAndConvertComponentStylePropsToStyle(
  componentStyleProps: ComponentStyleProps | undefined,
  callbackFn: (
    mediaSizeStyles: RuntimeStyleProperties,
    mediaSize: MediaSizeWithDefault,
  ) => RuntimeStyleProperties,
): RuntimeStylePropertiesWithMediaQueries {
  if (!componentStyleProps) {
    return {};
  }

  let styles: RuntimeStylePropertiesWithMediaQueries = {};

  // Base styles
  if (componentStyleProps?.style) {
    styles = callbackFn(componentStyleProps.style, "");
  }

  // Media sizes styles
  for (const mediaSize of supportedMediaSizes) {
    const mediaStyles = getMediaSizeStyles(componentStyleProps, mediaSize);
    if (mediaStyles) {
      styles[media[mediaSize]] = callbackFn(mediaStyles, mediaSize);
    }
  }

  return styles;
}

/**
 * Return the proper styles prop for the current media size
 * This function WILL only work correctly in the browser and
 * it's not intended to be used in the publisher
 */
export function getCurrentMediaSizeStyleProps(
  componentStyleProps: ComponentStyleProps,
  extras: {
    matcher: (query: string) => boolean;
    isEditor: boolean;
    canvas: EditorCanvas | null;
  },
) {
  if (
    extras.isEditor &&
    extras.canvas &&
    extras.canvas in editorCanvasToMediaSize
  ) {
    const mediaSizeStyles =
      componentStyleProps[
        `style@${editorCanvasToMediaSize[extras.canvas].mediaSize}`
      ];
    return mediaSizeStyles ?? componentStyleProps["style"];
  }

  for (const mediaSize of supportedMediaSizes) {
    if (extras.matcher(mediaQueries[mediaSize])) {
      const mediaSizeStyles = componentStyleProps[`style@${mediaSize}`];
      return mediaSizeStyles ?? componentStyleProps["style"];
    }
  }
  return componentStyleProps["style"];
}

// NOTE (Evan, 8/25/23) This returns some sensible defaults for height and width
// if the component is empty. Useful for empty containers, etc.
export function getDefaultStyleRulesIfEmpty(
  component: Component,
  styleProps: ComponentStyleProps,
  customSize?: { defaultMinWidth?: number; defaultMinHeight?: number },
) {
  return mapAndConvertComponentStylePropsToStyle(styleProps, (styles) => {
    const hasChildren = (component.children?.length ?? 0) > 0;
    const isGridChild = styles.__parentDisplay === "grid";

    const resolvedStyles: RuntimeStyleProperties = {};

    if (!styles.minWidth) {
      resolvedStyles.minWidth =
        !styles.__hasWidth && !hasChildren && !isGridChild
          ? (customSize?.defaultMinWidth ?? "200px")
          : "auto";
    }

    if (!styles.minHeight) {
      resolvedStyles.minHeight =
        !styles.__hasHeight && !hasChildren
          ? (customSize?.defaultMinHeight ?? "200px")
          : // Note (Ben O., 2025-05-08): We don't want to use "auto" here because if we hide
            // all children of a container, the container will collapse to 0px height and then you won't be able
            // to drag anything in to it.
            "1px";
    }

    return resolvedStyles;
  });
}

/**
 * Checks if the url is from cdn.shopify
 */
function isShopifySizeableImageUrl(imageUrl: string | undefined | null) {
  // if image url is already sized / suspicious to be sized we just ignore them
  if (
    !imageUrl ||
    /\d+x\d+/.test(imageUrl) ||
    imageUrl.includes("width=") ||
    imageUrl.includes("height=")
  ) {
    return false;
  }
  if (imageUrl.includes("cdn.shopify.com") || imageUrl.includes("/cdn/shop/")) {
    return !imageUrl.includes(".svg");
  }
  return false;
}

export const isShopifyOrReploSizeableImageUrl = (
  imageUrl: string | undefined | null,
) => {
  return (
    isShopifySizeableImageUrl(imageUrl) || isReploSizeableImageUrl(imageUrl)
  );
};
